// ============================================================================
// AUTH SERVICE - Cognito OAuth 2.0 Integration
// Handles authentication flow with AWS Cognito
// ============================================================================

import { Amplify } from 'aws-amplify';
import { signInWithRedirect, signOut, getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';

// Configure Amplify with Cognito settings
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || '',
      userPoolClientId: process.env.REACT_APP_COGNITO_CLIENT_ID || '',
      loginWith: {
        oauth: {
          domain: process.env.REACT_APP_COGNITO_DOMAIN || '',
          scopes: ['openid', 'email', 'profile'],
          redirectSignIn: [process.env.REACT_APP_COGNITO_REDIRECT_URI || 'http://localhost:3000/auth/callback'],
          redirectSignOut: [process.env.REACT_APP_COGNITO_LOGOUT_URI || 'http://localhost:3000/'],
          responseType: 'code'
        }
      }
    }
  }
});

export interface UserProfile {
  userId: string;
  email: string;
  displayName: string;
  firstName?: string;
  lastName?: string;
  organizationId: string;
  role: 'normal' | 'read_only' | 'avatar_admin' | 'template_admin' | 'compliance_user' | 'compliance_admin' | 'system_admin' | 'admin' | 'project_manager' | 'workflow_admin' | 'compliance_officer' | 'tier1_facilitator' | 'tier2_facilitator';
  enabledFeatures?: string[];
  navPreferences?: { pinnedItems: string[] };
}

/**
 * Initiate OAuth 2.0 login flow
 * Redirects to Cognito Hosted UI
 */
export const login = async (): Promise<void> => {
  try {
    await signInWithRedirect();
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

/**
 * Logout user and clear session
 */
export const logout = async (): Promise<void> => {
  try {
    await signOut();
  } catch (error) {
    console.error('Logout error:', error);
    throw error;
  }
};

/**
 * Get current authenticated user
 */
export const getCurrentAuthenticatedUser = async (): Promise<any> => {
  try {
    const user = await getCurrentUser();
    return user;
  } catch (error) {
    console.error('Get current user error:', error);
    return null;
  }
};

/**
 * Get current session with tokens
 */
export const getSession = async (): Promise<any> => {
  try {
    const session = await fetchAuthSession();
    return session;
  } catch (error) {
    console.error('Get session error:', error);
    return null;
  }
};

/**
 * Get ID token (JWT) for API requests
 */
export const getIdToken = async (): Promise<string | null> => {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || null;
  } catch (error) {
    console.error('Get ID token error:', error);
    return null;
  }
};

/**
 * Fetch user profile from backend
 */
export const fetchUserProfile = async (idToken: string): Promise<UserProfile | null> => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/users/me`, {
      headers: {
        'Authorization': `Bearer ${idToken}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch user profile');
    }

    const data = await response.json();
    return data.user;
  } catch (error) {
    console.error('Fetch user profile error:', error);
    return null;
  }
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = async (): Promise<boolean> => {
  try {
    const session = await getSession();
    return !!session?.tokens?.idToken;
  } catch (error) {
    return false;
  }
};
